import { eftPath } from "@cle/constants/routes";
import type { PeriodeLektioner } from "./efteruddannelse";
import { PaabudStatus, type PaabudDetailModel } from "./paabud";
import type { PaabudIndberetningOverviewModel } from "./paabud";
import { PaalaegStatus, type PaalaegDetailModel } from "./paalaeg";
import type { PaalaegIndberetningOverviewModel } from "./paalaeg";

export type AfgorelseType = "paabud" | "paalaeg";

export type AfgorelseDetailModel = {
  id: string;
  indberetningId: string | null;
  paakraevedeLektioner: number;
  periodeFra: string;
  /** Null når forløbet er i bero og perioden derfor ikke er udløbet. */
  periodeTil: string | null;
  /** Periodens sidste dag — den, der skal vises. Null når perioden ikke er udløbet. */
  periodeTilMinusEnDag: string | null;
  /** Null betyder i bero: fristen løber kun, mens advokaten er udøvende. */
  frist: string | null;
  decisionDocumentFileName: string;
  /**
   * Afgørelsens egen status. Lukket sættes både når afgørelsen er opfyldt, og når den
   * henlægges fordi advokaten er død — de to skelnes på, om der er indsendt en indberetning.
   * Påbud og pålæg har hver sin enum med identiske værdier, og TypeScript-enums er nominelle,
   * så typen må være en union frem for den ene af dem.
   */
  status: PaabudStatus | PaalaegStatus;
  harAabenIndberetning: boolean;
  indsendt: string | null;
  lektionerIndberettet: PeriodeLektioner | null;
  lektionerOverfoeresTilNaeste: PeriodeLektioner | null;
};

export type AfgorelseOverviewModel =
  | (PaabudIndberetningOverviewModel & { afgorelseType: "paabud" })
  | (PaalaegIndberetningOverviewModel & { afgorelseType: "paalaeg" });

export type AfgorelseLabels = {
  entityName: string;
  entityNameCapitalized: string;
  documentationTitle: string;
  overviewStepTitle: string;
  overviewStepDescription: string;
  submitDialogDescription: string;
  progressCardTitle: string;
  decisionDocumentFallback: string;
  noOpenIndberetningMessage: string;
  submittedHeroSubtitle: string;
  submittedHeroDescription: string;
  submittedSummaryTitle: string;
  submittedSummaryDescription: string;
  submittedDocumentsHeader: string;
  submittedDocumentsDescription: string;
  submittedTimelineDescription: string;
  overviewHeader: string;
  pendingSubmittedColumnLabel: string;
  downloadFallbackFileName: string;
  downloadButtonLabel: string;
};

export const AFGOERELSE_LABELS: Record<AfgorelseType, AfgorelseLabels> = {
  paabud: {
    entityName: "påbud",
    entityNameCapitalized: "Påbud",
    documentationTitle: "Dokumentation for påbud",
    overviewStepTitle: "Overblik over påbud",
    overviewStepDescription:
      "Gennemgå de valgte lektioner, før du indsender påbuddet.",
    submitDialogDescription: "Godkend og indsend dokumentation for påbud.",
    progressCardTitle: "Påbud",
    decisionDocumentFallback: "Påbud om efteruddannelse",
    noOpenIndberetningMessage:
      "Der er ingen åben indberetning for dette påbud.",
    submittedHeroSubtitle: "Påbud",
    submittedHeroDescription:
      "Dokumentationen for påbuddet er indsendt. Her kan du se de vigtigste oplysninger om indsendelsen.",
    submittedSummaryTitle: "Information om påbuddet",
    submittedSummaryDescription: "Påbud om efteruddannelse",
    submittedDocumentsHeader: "Dokumenter brugt i påbuddet",
    submittedDocumentsDescription:
      "Dokumenter, der er medtaget i den indsendte dokumentation.",
    submittedTimelineDescription:
      "Denne side viser en opsummering af dokumentationen, der allerede er indsendt på påbuddet.",
    overviewHeader: "Overblik over påbud",
    pendingSubmittedColumnLabel: "Indberettes for påbud",
    downloadFallbackFileName: "afgoerelse-for-paabud.pdf",
    downloadButtonLabel: "afgørelse",
  },
  paalaeg: {
    entityName: "pålæg",
    entityNameCapitalized: "Pålæg",
    documentationTitle: "Dokumentation for pålæg",
    overviewStepTitle: "Overblik over pålæg",
    overviewStepDescription:
      "Gennemgå de valgte lektioner, før du indsender pålægget.",
    submitDialogDescription: "Godkend og indsend dokumentation for pålæg.",
    progressCardTitle: "Pålæg",
    decisionDocumentFallback: "Pålæg om efteruddannelse",
    noOpenIndberetningMessage:
      "Der er ingen åben indberetning for dette pålæg.",
    submittedHeroSubtitle: "Pålæg",
    submittedHeroDescription:
      "Dokumentationen for pålægget er indsendt. Her kan du se de vigtigste oplysninger om indsendelsen.",
    submittedSummaryTitle: "Information om pålægget",
    submittedSummaryDescription: "Pålæg om efteruddannelse",
    submittedDocumentsHeader: "Dokumenter brugt i pålægget",
    submittedDocumentsDescription:
      "Dokumenter, der er medtaget i den indsendte dokumentation.",
    submittedTimelineDescription:
      "Denne side viser en opsummering af dokumentationen, der allerede er indsendt på pålægget.",
    overviewHeader: "Overblik over pålæg",
    pendingSubmittedColumnLabel: "Indberettes for pålæg",
    downloadFallbackFileName: "kendelse-for-pålæg.pdf",
    downloadButtonLabel: "kendelse",
  },
};

export const getAfgorelseOverviewId = (item: AfgorelseOverviewModel): string =>
  item.afgorelseType === "paabud" ? item.paabudId : item.paalaegId;

export const getCleAfgorelseId = (
  afgorelseType: AfgorelseType,
  detail: PaabudDetailModel | PaalaegDetailModel,
): string =>
  afgorelseType === "paabud"
    ? (detail as PaabudDetailModel).aikaPaabudId
    : (detail as PaalaegDetailModel).aikaPaalaegId;

export const getAfgorelseDetailPath = (
  type: AfgorelseType,
  id: string,
): string => eftPath(`afgorelser/${type}/${id}`);
